/* eslint-disable complexity */
/* eslint-disable max-len */
import React from 'react';
import i18n from '@i18next/i18n';
import Menu from '@tencent/dui/lib/components/Menu';
import Checkbox from '@tencent/dui/lib/components/Checkbox';
import Dropdown from '@tencent/dui/lib/components/Dropdown';
import some from 'lodash/some';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { STDLblPos, STLegendPos, STTrendlineType, SubChartType } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import {
    getAvailableDataLabelPositions,
    getDataLabelDefaultPosition,
} from '@src/slide/domain/ui-model/element-modeles/chart-models/utils/data-label';
import { DataLabelPositionName, GridlinesType } from '@src/slide/domain/ui-model/element-modeles/constant/chart';
import { isTrendlineAvailable } from '@src/slide/domain/ui-model/element-modeles/chart-models/utils/trendline';
import Arrow from '../../../editor/src/img/menu/arrow.svg';
import PStyle from './../palette-panel/palette/index.less';
import type { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import type { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import type { IChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-model.interface';
import { IGridLineProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/grid-line.interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IChartTrendlineManager } from '@src/slide/application/service/chart-manager/chart-manager.interface';

interface IChartComponentEditPanelProps {
    /**
     * 上层菜单关闭方法
     */
    hideMenu: () => void;
    chartTrendlineManager?: IDynamicService<IChartTrendlineManager>;
}

export enum ChartActionType {
    AXIS = 'axis',
    AXIS_TITLE = 'axisTitle',
    TITLE = 'title',
    DATA_LABELS = 'dataLabels',
    LEGEND = 'legend',
    GRIDLINES = 'gridlines',
    TRENDLINE = 'trendline',
}

enum ActionOption {
    ALL_AXIS = 'allAxis',
    ALL_AXIS_TITLE = 'allAxisTitle',
    ALL_GRIDLINES = 'allGridlines',
}

enum ItemType {
    CHECKBOX = 'checkbox',
    ITEM = 'item',
}

export default class ChartComponentEditPanel extends React.Component<IChartComponentEditPanelProps> {
    public onCheckChange = (actionType, value: boolean, option?) => {
        this.report(actionType, value, option);
        const { selectedArray } = this.getSelectionService().selectedElements;
        if (selectedArray?.length === 1) {
            const model = selectedArray[0] as IChartModel;
            const { chartProperty } = model;
            const {
                axes: axesProperty,
            } = chartProperty;
            switch (actionType) {
                case ChartActionType.AXIS: {
                    if (option === ActionOption.ALL_AXIS) {
                        value ? chartProperty?.createAllAxes() : chartProperty?.removeAllAxes();
                        return;
                    }
                    const axis = axesProperty?.getAxisByName(option);
                    value ? axis?.showAxis() : axis?.hideAxis();
                    break;
                }
                case ChartActionType.AXIS_TITLE: {
                    if (option === ActionOption.ALL_AXIS_TITLE) {
                        value ? chartProperty?.createAllAxesTitle() : chartProperty?.removeAllAxesTitle();
                        return;
                    }
                    const axis = axesProperty?.getAxisByName(option);
                    value ? axis?.createAxisTitle() : axis?.removeAxisTitle();
                    break;
                }
                case ChartActionType.TITLE: {
                    value ? chartProperty.createTitle() : chartProperty?.removeTitle();
                    break;
                }
                case ChartActionType.DATA_LABELS: {
                    if (value) {
                        const position =  getDataLabelDefaultPosition(model);
                        chartProperty.updateDataLabels({ labelPosition: position });
                    } else {
                        chartProperty.removeDataLabels();
                    }
                    break;
                }
                case ChartActionType.LEGEND: {
                    value ? chartProperty.createLegend() : chartProperty?.removeLegend();
                    break;
                }
                case ChartActionType.GRIDLINES: {
                    if (option === ActionOption.ALL_GRIDLINES) {
                        value ? chartProperty?.createAllAxesGridlines() : chartProperty?.removeAllAxesGridlines();
                        return;
                    }
                    const axis = axesProperty?.getAxisByName(option.axis);
                    value ? axis?.createGridlines([option.gridlines]) : axis?.removeGridlines([option.gridlines]);
                    break;
                }
                case ChartActionType.TRENDLINE: {
                    if (value) {
                        this.props.chartTrendlineManager?.promise().then((chartTrendlineManager) => {
                            chartTrendlineManager.showSeriesSelectModal(model);
                        });
                    } else {
                        chartProperty.removeTrendlines();
                    }
                    break;
                }
                default:
                    break;
            }
        }
    };

    public onItemClick = (actionType, value) => {
        this.report(actionType, value);
        const { selectedArray } = this.getSelectionService().selectedElements;
        if (selectedArray?.length === 1) {
            const model = selectedArray[0] as IChartModel;
            const { chartProperty } = model;
            const {
                chartTitle: chartTitleProperty,
                legend: legendProperty,
            } = chartProperty;
            switch (actionType) {
                case ChartActionType.TITLE: {
                    if (chartTitleProperty) {
                        chartTitleProperty.updateProps(
                            { overlay: value === 'overlay' },
                            { layout: null }, // 修改是否覆盖，需要清空layout
                        );
                    } else {
                        // 没有title，直接设置属性
                        chartProperty.createTitle({ overlay: value === 'overlay' });
                    }
                    break;
                }
                case ChartActionType.DATA_LABELS: {
                    chartProperty.updateDataLabels({ labelPosition: value });
                    break;
                }
                case ChartActionType.LEGEND: {
                    if (legendProperty) {
                        legendProperty.updateProps({ position: value }, { layout: null });
                    } else {
                        chartProperty.createLegend({ position: value });
                    }
                    break;
                }
                case ChartActionType.TRENDLINE: {
                    this.props.chartTrendlineManager?.promise().then((chartTrendlineManager) => {
                        chartTrendlineManager.showSeriesSelectModal(model, value);
                    });
                    break;
                }
                default:
                    break;
            }
        }
    };

    public onSubMenuClick = (e) => {
        // 阻止冒泡， 防止上层dropdown挂载的全局事件关闭菜单
        e.nativeEvent.stopImmediatePropagation();
    };

    public getMenuData = (model: IChartModel) => {
        const { charts } = model.plotArea;
        const chartType = charts[0]?.type;

        /** 坐标轴数据 */
        const axesProperty = model.chartProperty.axes!;
        const hasAxes = axesProperty.axisList.some(axis => !!axis);
        const axes = axesProperty.getAllAxes();
        const [
            primaryHorizontalAxis,
            primaryVerticalAxis,
            secondaryHorizontalAxis,
            secondaryVerticalAxis,
        ] = axes;

        /** 图表标题数据 */
        const titleProperty = model.chartProperty.chartTitle;
        const hasChartTitle = !!model.chartProperty?.chartTitle;
        const chartTitleOverlay = titleProperty?.overlay;


        const legendProperty = model.chartProperty.legend;
        const hasLegend = !!legendProperty;
        const hasLegendLayout = !!legendProperty?.layout;
        // 有自定义布局的时候 不勾选位置，参照office
        const legendPos = hasLegend && !hasLegendLayout ? legendProperty.position : '';

        const allSeries = charts.map(chart => chart.series)
            .flat();
        const hasDatalabel = allSeries.some(series => series.dataLabels && !series.dataLabels.delete);
        const dataLabelPosition = hasDatalabel
            ? this.getDataLabelPosition(model) || getDataLabelDefaultPosition(model)
            : undefined;
        // todo 组合图按照第一个子图取
        const createChartType = charts[0]?.getPresetChartType();
        const isStock = charts.some(chart => chart.type === SubChartType.StockChart);
        const hasGrid = chartType === SubChartType.RadarChart
            ? primaryVerticalAxis && (this.hasGridline(primaryVerticalAxis.majorGridlines) || this.hasGridline(primaryVerticalAxis.minorGridlines))
            : some(
                axes,
                axis => this.hasGridline(axis?.majorGridlines) || this.hasGridline(axis?.minorGridlines),
            );

        return [
            {
                name: i18n.t('坐标轴'),
                type: ChartActionType.AXIS,
                id: ActionOption.ALL_AXIS,
                itemShow: hasAxes,
                itemValue: some(axes, axis => axis && !axis.isDelete),
                children: [
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('主要横坐标轴'),
                        id: primaryHorizontalAxis?.name,
                        itemShow: primaryHorizontalAxis,
                        itemValue: !primaryHorizontalAxis?.isDelete,
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('主要纵坐标轴'),
                        id: primaryVerticalAxis?.name,
                        itemShow: primaryVerticalAxis,
                        itemValue: !primaryVerticalAxis?.isDelete,
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('次要横坐标轴'),
                        id: secondaryHorizontalAxis?.name,
                        itemShow: secondaryHorizontalAxis,
                        itemValue: !secondaryHorizontalAxis?.isDelete,
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('次要纵坐标轴'),
                        id: secondaryVerticalAxis?.name,
                        itemShow: secondaryVerticalAxis,
                        itemValue: !secondaryVerticalAxis?.isDelete,
                    },
                ],
            },
            {
                name: i18n.t('轴标题'),
                type: ChartActionType.AXIS_TITLE,
                id: ActionOption.ALL_AXIS_TITLE,
                itemShow: hasAxes && chartType !== SubChartType.RadarChart,
                itemValue: some(axes, axis => axis?.title),
                children: [
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('主要横坐标轴'),
                        id: primaryHorizontalAxis?.name,
                        itemShow: primaryHorizontalAxis,
                        itemValue: !!primaryHorizontalAxis?.title,
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('主要纵坐标轴'),
                        id: primaryVerticalAxis?.name,
                        itemShow: primaryVerticalAxis,
                        itemValue: !!primaryVerticalAxis?.title,
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('次要横坐标轴'),
                        id: secondaryHorizontalAxis?.name,
                        itemShow: secondaryHorizontalAxis,
                        itemValue: !!secondaryHorizontalAxis?.title,
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('次要纵坐标轴'),
                        id: secondaryVerticalAxis?.name,
                        itemShow: secondaryVerticalAxis,
                        itemValue: !!secondaryVerticalAxis?.title,
                    },
                ],
            },
            {
                name: i18n.t('图表标题'),
                type: ChartActionType.TITLE,
                id: ChartActionType.TITLE,
                itemShow: true,
                itemValue: hasChartTitle,
                children: [
                    {
                        itemType: ItemType.ITEM,
                        name: i18n.t('图表上方'),
                        id: 'notOverlay',
                        itemShow: true,
                        selected: hasChartTitle && !chartTitleOverlay,
                    },
                    {
                        itemType: ItemType.ITEM,
                        name: i18n.t('居中覆盖'),
                        id: 'overlay',
                        itemShow: true,
                        selected: hasChartTitle && chartTitleOverlay,
                    },
                ],
            },
            {
                name: i18n.t('数据标签'),
                type: ChartActionType.DATA_LABELS,
                id: ChartActionType.DATA_LABELS,
                itemShow: true,
                itemValue: allSeries.some(series => series.dataLabels && !series.dataLabels.delete),
                children:
                    getAvailableDataLabelPositions(createChartType).length > 0 && !isStock
                        ? [
                            {
                                itemType: ItemType.ITEM,
                                name: DataLabelPositionName[STDLblPos.ctr],
                                id: STDLblPos.ctr,
                                itemShow: getAvailableDataLabelPositions(createChartType)
                                    .includes(STDLblPos.ctr),
                                selected: dataLabelPosition === STDLblPos.ctr,
                            },
                            {
                                itemType: ItemType.ITEM,
                                name: DataLabelPositionName[STDLblPos.inEnd],
                                id: STDLblPos.inEnd,
                                itemShow: getAvailableDataLabelPositions(createChartType)
                                    .includes(STDLblPos.inEnd),
                                selected: dataLabelPosition === STDLblPos.inEnd,
                            },
                            {
                                itemType: ItemType.ITEM,
                                name: DataLabelPositionName[STDLblPos.inBase],
                                id: STDLblPos.inBase,
                                itemShow: getAvailableDataLabelPositions(createChartType)
                                    .includes(STDLblPos.inBase),
                                selected: dataLabelPosition === STDLblPos.inBase,
                            },
                            {
                                itemType: ItemType.ITEM,
                                name: DataLabelPositionName[STDLblPos.outEnd],
                                id: STDLblPos.outEnd,
                                itemShow: getAvailableDataLabelPositions(createChartType)
                                    .includes(STDLblPos.outEnd),
                                selected: dataLabelPosition === STDLblPos.outEnd,
                            },
                            {
                                itemType: ItemType.ITEM,
                                name: DataLabelPositionName[STDLblPos.l],
                                id: STDLblPos.l,
                                itemShow: getAvailableDataLabelPositions(createChartType)
                                    .includes(STDLblPos.l),
                                selected: dataLabelPosition === STDLblPos.l,
                            },
                            {
                                itemType: ItemType.ITEM,
                                name: DataLabelPositionName[STDLblPos.r],
                                id: STDLblPos.r,
                                itemShow: getAvailableDataLabelPositions(createChartType)
                                    .includes(STDLblPos.r),
                                selected: dataLabelPosition === STDLblPos.r,
                            },
                            {
                                itemType: ItemType.ITEM,
                                name: DataLabelPositionName[STDLblPos.t],
                                id: STDLblPos.t,
                                itemShow: getAvailableDataLabelPositions(createChartType)
                                    .includes(STDLblPos.t),
                                selected: dataLabelPosition === STDLblPos.t,
                            },
                            {
                                itemType: ItemType.ITEM,
                                name: DataLabelPositionName[STDLblPos.b],
                                id: STDLblPos.b,
                                itemShow: getAvailableDataLabelPositions(createChartType)
                                    .includes(STDLblPos.b),
                                selected: dataLabelPosition === STDLblPos.b,
                            },
                            {
                                itemType: ItemType.ITEM,
                                name: DataLabelPositionName[STDLblPos.bestFit],
                                id: STDLblPos.bestFit,
                                itemShow: getAvailableDataLabelPositions(createChartType)
                                    .includes(STDLblPos.bestFit),
                                selected: dataLabelPosition === STDLblPos.bestFit,
                            },
                        ]
                        : [],
            },
            {
                name: i18n.t('网格线'),
                type: ChartActionType.GRIDLINES,
                id: ActionOption.ALL_GRIDLINES,
                itemShow: hasAxes,
                itemValue: !!hasGrid,
                children: [
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('主轴主要水平网格线'),
                        id: 'primaryVerticalAxisMajorGridlines',
                        itemShow: primaryVerticalAxis,
                        itemValue: this.hasGridline(primaryVerticalAxis?.majorGridlines),
                        option: {
                            axis: primaryVerticalAxis?.name,
                            gridlines: GridlinesType.MAJOR_GRID,
                        },
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('主轴主要垂直网格线'),
                        id: 'primaryHorizontalAxisMajorGridlines',
                        itemShow: primaryHorizontalAxis && chartType !== SubChartType.RadarChart,
                        itemValue: this.hasGridline(primaryHorizontalAxis?.majorGridlines),
                        option: {
                            axis: primaryHorizontalAxis?.name,
                            gridlines: GridlinesType.MAJOR_GRID,
                        },
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('主轴次要水平网格线'),
                        id: 'primaryVerticalAxisMinorGridlines',
                        itemShow: primaryVerticalAxis,
                        itemValue: this.hasGridline(primaryVerticalAxis?.minorGridlines),
                        option: {
                            axis: primaryVerticalAxis?.name,
                            gridlines: GridlinesType.MINOR_GRID,
                        },
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('主轴次要垂直网格线'),
                        id: 'primaryHorizontalAxisMinorGridlines',
                        itemShow: primaryHorizontalAxis && chartType !== SubChartType.RadarChart,
                        itemValue: this.hasGridline(primaryHorizontalAxis?.minorGridlines),
                        option: {
                            axis: primaryHorizontalAxis?.name,
                            gridlines: GridlinesType.MINOR_GRID,
                        },
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('次轴主要水平网格线'),
                        id: 'secondaryVerticalAxisMajorGridlines',
                        itemShow: secondaryVerticalAxis,
                        itemValue: this.hasGridline(secondaryVerticalAxis?.majorGridlines),
                        option: {
                            axis: secondaryVerticalAxis?.name,
                            gridlines: GridlinesType.MAJOR_GRID,
                        },
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('次轴主要垂直网格线'),
                        id: 'secondaryHorizontalAxisMajorGridlines',
                        itemShow: secondaryHorizontalAxis && chartType !== SubChartType.RadarChart,
                        itemValue: this.hasGridline(secondaryHorizontalAxis?.majorGridlines),
                        option: {
                            axis: secondaryHorizontalAxis?.name,
                            gridlines: GridlinesType.MAJOR_GRID,
                        },
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('次轴次要水平网格线'),
                        id: 'secondaryVerticalAxisMinorGridlines',
                        itemShow: secondaryVerticalAxis,
                        itemValue: this.hasGridline(secondaryVerticalAxis?.minorGridlines),
                        option: {
                            axis: secondaryVerticalAxis?.name,
                            gridlines: GridlinesType.MINOR_GRID,
                        },
                    },
                    {
                        itemType: ItemType.CHECKBOX,
                        name: i18n.t('次轴次要垂直网格线'),
                        id: 'secondaryHorizontalAxisMinorGridlines',
                        itemShow: secondaryHorizontalAxis && chartType !== SubChartType.RadarChart,
                        itemValue: this.hasGridline(secondaryHorizontalAxis?.minorGridlines),
                        option: {
                            axis: secondaryHorizontalAxis?.name,
                            gridlines: GridlinesType.MINOR_GRID,
                        },
                    },
                ],
            },
            {
                name: i18n.t('图例'),
                type: ChartActionType.LEGEND,
                id: ChartActionType.LEGEND,
                itemShow: true,
                itemValue: hasLegend,
                children: [
                    {
                        itemType: ItemType.ITEM,
                        name: i18n.t('靠上'),
                        id: STLegendPos.t,
                        itemShow: true,
                        selected: legendPos === STLegendPos.t,
                    },
                    {
                        itemType: ItemType.ITEM,
                        name: i18n.t('靠下'),
                        id: STLegendPos.b,
                        itemShow: true,
                        selected: legendPos === STLegendPos.b,
                    },
                    {
                        itemType: ItemType.ITEM,
                        name: i18n.t('靠左'),
                        id: STLegendPos.l,
                        itemShow: true,
                        selected: legendPos === STLegendPos.l,
                    },
                    {
                        itemType: ItemType.ITEM,
                        name: i18n.t('靠右'),
                        id: STLegendPos.r,
                        itemShow: true,
                        selected: legendPos === STLegendPos.r,
                    },
                    {
                        itemType: ItemType.ITEM,
                        name: i18n.t('右上'),
                        id: STLegendPos.tr,
                        itemShow: true,
                        selected: legendPos === STLegendPos.tr,
                    },
                ],
            },
            {
                name: i18n.t('趋势线'),
                type: ChartActionType.TRENDLINE,
                id: ChartActionType.TRENDLINE,
                itemShow: isTrendlineAvailable(model),
                // 目前只支持线性和指数类型的趋势线
                itemValue: allSeries.some(series => series.trendlines?.some?.(trendline => trendline?.type === STTrendlineType.linear || trendline?.type === STTrendlineType.exp)),
                children: [
                    {
                        itemType: ItemType.ITEM,
                        name: i18n.t('线性'),
                        id: STTrendlineType.linear,
                        itemShow: true,
                        hideMenu: true,
                    },
                    {
                        itemType: ItemType.ITEM,
                        name: i18n.t('指数'),
                        id: STTrendlineType.exp,
                        itemShow: true,
                        hideMenu: true,
                    },
                ],
            },
        ];
    };

    public render(): React.ReactNode {
        const { selectedArray } = this.getSelectionService().selectedElements;
        if (!selectedArray) return null;
        const model = selectedArray[0] as IChartModel;
        const menuData = this.getMenuData(model);

        return (
            <Menu className='chart-component-menu'>
                {menuData.map(item => (item.itemShow ? (
                    <Menu.Item key={item.id}>
                        <Checkbox
                            onChange={value => this.onCheckChange(item.type, value, item.id)}
                            checked={!!item.itemValue}
                            style={{ width: '100%' }}
                        >
                            {item.name}
                        </Checkbox>
                        {item.children?.length ? (
                            <Dropdown
                                dropContent={
                                    <Dropdown.Keep onClick={this.onSubMenuClick}>
                                        <Menu className={`chart-${item.type}-submenu`}>
                                            {item.children.map((subItem) => {
                                                if (!subItem.itemShow) return null;
                                                if (subItem.itemType === ItemType.CHECKBOX) {
                                                    return (
                                                        <Menu.Item key={subItem.id}>
                                                            <Checkbox
                                                                onChange={value => this.onCheckChange(
                                                                    item.type,
                                                                    value,
                                                                    subItem.option || subItem.id,
                                                                )
                                                                }
                                                                checked={!!subItem.itemValue}
                                                                style={{ fontSize: 12 }}
                                                            >
                                                                {subItem.name}
                                                            </Checkbox>
                                                        </Menu.Item>
                                                    );
                                                }
                                                if (subItem.itemType === ItemType.ITEM) {
                                                    return (
                                                        <Menu.Item
                                                            key={subItem.id}
                                                            selected={subItem.selected}
                                                            onClick={(e) => {
                                                                if (subItem.hideMenu) {
                                                                    this.props.hideMenu?.();
                                                                }
                                                                this.onItemClick(
                                                                    item.type,
                                                                    subItem.option || subItem.id,
                                                                );
                                                            }}
                                                        >
                                                            {subItem.name}
                                                        </Menu.Item>
                                                    );
                                                }
                                            })}
                                        </Menu>
                                    </Dropdown.Keep>
                                }
                            >
                                <div style={{ margin: 5 }}>
                                    <img src={Arrow} className={PStyle['list-item-arrow']} />
                                </div>
                            </Dropdown>
                        ) : null}
                    </Menu.Item>
                ) : null))}
            </Menu>
        );
    }

    private getSelectionService(): ISelectionService {
        return (AppServiceProvider.getInstance()
            .getAppServices() as IEditorAppServices).viewableServiceLayer
            .selectionService;
    }

    private getDataLabelPosition(model: IChartModel) {
        const { charts } = model.plotArea;
        const allSeries = charts.map(chart => chart.series)
            .flat();
        const dataLabelPositions = [...new Set(allSeries.map(ser => ser.dataLabels?.labelPosition))].filter(i => i);
        if (dataLabelPositions.length > 1) return;
        // 如果series下有统一的数据标签位置， 则返回该位置
        if (dataLabelPositions.length === 1) {
            return dataLabelPositions[0];
        }
        if (dataLabelPositions.length === 0) {
            // 如果series下没有数据标签位置， charts下有统一的数据标签位置， 则返回该位置
            const chartDataLabels = [...new Set(charts.map(chart => chart.dataLabels?.labelPosition))].filter(i => i);
            if (chartDataLabels.length > 1) return;
            if (chartDataLabels.length === 1) {
                return chartDataLabels[0];
            }
        }
    }

    private hasGridline(gridline?: IGridLineProperty) {
        return gridline && !gridline.props?.style.border?.noFill;
    }

    private report(actionType, value, option?) {
        const ver6Param = JSON.stringify({
            actionType,
            value,
            option,
        });

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.CHART_TOOLBAR,
            action: TDWActionType.CHART_ELEMENT,
            ver6: ver6Param,
        });
    }
}
